import React, { useState } from 'react';
import { relationshipsAPI, RelationshipDto, PersonDto } from '../services/api';
import { Button, ModalFooter, ModalHeader, Select } from './common';
import { useTranslation } from 'react-i18next';
import { useRelationshipLabels } from '../utils/useRelationshipLabels';
import { trackEvent } from '../services/analytics';

interface EditRelationshipModalProps {
  relationship: RelationshipDto;
  people: PersonDto[];
  onClose: () => void;
  onUpdated?: () => void;
  onDeleted?: () => void;
}

const BIDIRECTIONAL_ARROW = '\u2194';

export const EditRelationshipModal: React.FC<EditRelationshipModalProps> = ({
  relationship,
  people,
  onClose,
  onUpdated,
  onDeleted,
}) => {
  const { t } = useTranslation();
  const { toDisplayRelationshipLabel } = useRelationshipLabels();
  const translatedRelationshipTypes = [
    { value: 'Parent', label: t('relationship.types.parent', { ns: 'canvas' }) },
    { value: 'StepFather', label: t('relationship.types.stepFather', { ns: 'canvas' }) },
    { value: 'StepMother', label: t('relationship.types.stepMother', { ns: 'canvas' }) },
    { value: 'Child', label: t('relationship.types.child', { ns: 'canvas' }) },
    { value: 'Spouse', label: t('relationship.types.spouse', { ns: 'canvas' }) },
    { value: 'Sibling', label: t('relationship.types.sibling', { ns: 'canvas' }) },
    { value: 'StepSibling', label: t('relationship.types.stepSibling', { ns: 'canvas' }) },
    { value: 'Aunt', label: t('relationship.types.aunt', { ns: 'canvas' }) },
    { value: 'Uncle', label: t('relationship.types.uncle', { ns: 'canvas' }) },
    { value: 'Cousin', label: t('relationship.types.cousin', { ns: 'canvas' }) },
    { value: 'Other', label: t('relationship.types.other', { ns: 'canvas' }) },
  ];
  const [type, setType] = useState(relationship.type || 'Other');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const canEditRelationship = Boolean(relationship.canEdit);
  const canDeleteRelationship = Boolean(relationship.canDelete);
  const isReadOnly = !canEditRelationship && !canDeleteRelationship;

  const personA = people.find((p) => p.id === relationship.personAId);
  const personB = people.find((p) => p.id === relationship.personBId);

  const handleUpdate = async () => {
    try {
      setLoading(true);
      setError('');
      await relationshipsAPI.updateRelationship(relationship.id, { type });
      trackEvent('relationship_edited', { relationship_type: type });
      onUpdated?.();
      onClose();
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        t('relationship.failedToUpdate', { ns: 'canvas' });
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      setError('');
      await relationshipsAPI.deleteRelationship(relationship.id);
      onDeleted?.();
      onClose();
    } catch (err) {
      setError(t('relationship.failedToDelete', { ns: 'canvas' }));
    } finally {
      setLoading(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(26,23,20,0.45)] px-4 py-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-[24px] border border-[#E2DDD8] bg-[#FFFFFF] shadow-[0_24px_60px_rgba(26,23,20,0.24)]"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader
          eyebrow={t('relationship.editEyebrow', { ns: 'canvas' })}
          title={isReadOnly ? t('relationship.detailsTitle', { ns: 'canvas' }) : t('relationship.editTitle', { ns: 'canvas' })}
          description={t('relationship.editDescription', { ns: 'canvas' })}
          onClose={onClose}
          closeAriaLabel={t('relationship.closeEditAriaLabel', { ns: 'canvas' })}
        />

        <div className="space-y-6 px-6 py-5">
          {error && (
            <div className="rounded-xl border border-[#F7C1C1] bg-[#FCEBEB] px-4 py-3 text-sm text-[#A32D2D]">
              {error}
            </div>
          )}

          {isReadOnly && (
            <div className="rounded-xl border border-[#E2DDD8] bg-[#F9F7F4] px-4 py-3 text-sm text-[#4A443E]">
              {t('relationship.noPermission', { ns: 'canvas' })}
            </div>
          )}

          <div className="rounded-2xl border border-[#E2DDD8] bg-[#F9F7F4] px-4 py-4 text-center">
            <p className="text-[#4A443E]">
              <span className="font-semibold">
                {personA ? `${personA.firstName} ${personA.lastName}` : 'Unknown'}
              </span>{' '}
              {BIDIRECTIONAL_ARROW}{' '}
              <span className="font-semibold">
                {personB ? `${personB.firstName} ${personB.lastName}` : 'Unknown'}
              </span>
            </p>
          </div>

          <div>
            {canEditRelationship ? (
              <Select
                label={t('relationship.typeLabel', { ns: 'canvas' })}
                value={type}
                onChange={(e) => setType(e.target.value)}
                disabled={loading}
                options={translatedRelationshipTypes}
              />
            ) : (
              <div className="w-full rounded-xl border border-[#E2DDD8] bg-[#F9F7F4] px-4 py-2.5 text-sm text-[#4A443E]">
                {toDisplayRelationshipLabel(type)}
              </div>
            )}
          </div>

        </div>
        <ModalFooter>
            {confirmDelete ? (
              <>
                <Button
                  onClick={() => setConfirmDelete(false)}
                  variant="outline"
                  className="sm:min-w-[140px]"
                >
                  {t('buttons.cancel', { ns: 'common' })}
                </Button>
                <Button
                  onClick={handleDelete}
                  variant="danger"
                  disabled={loading}
                  className="sm:min-w-[180px]"
                >
                  {loading ? t('relationship.deletingLabel', { ns: 'canvas' }) : t('relationship.confirmDeleteLabel', { ns: 'canvas' })}
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="sm:min-w-[140px]"
                >
                  {t('buttons.cancel', { ns: 'common' })}
                </Button>
                {canDeleteRelationship ? (
                  <Button
                    onClick={() => setConfirmDelete(true)}
                    variant="secondary"
                    className="text-[#A32D2D] border-[#E9B6B0] bg-[#FCEBEB] hover:bg-[#F8DDDD] hover:text-[#A32D2D] sm:min-w-[140px]"
                  >
                    {t('buttons.delete', { ns: 'common' })}
                  </Button>
                ) : null}
                {canEditRelationship ? (
                  <Button
                    onClick={handleUpdate}
                    disabled={loading}
                    className="sm:min-w-[160px]"
                  >
                    {loading ? t('relationship.savingLabel', { ns: 'canvas' }) : t('relationship.saveLabel', { ns: 'canvas' })}
                  </Button>
                ) : null}
              </>
            )}
        </ModalFooter>
      </div>
    </div>
  );
};
